import { useCallback, useEffect, useState } from 'react'
import { Button, Message } from '@arco-design/web-react'
import { IconHistory } from '@arco-design/web-react/icon'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { getUserInfoApi } from '@/api/user'
import {
  getTopupInfoApi,
  redeemCodeApi,
  createPayOrderApi,
  createStripeOrderApi,
  createCreemOrderApi,
  createWaffoOrderApi,
  submitEpayForm,
  getTopupReturnUrls,
  type TopupInfo,
} from '@/api/topup'
import PageHeader from '@/components/Layout/PageHeader'
import AccountStatsCard from '@/components/topup/AccountStatsCard'
import RedeemCard from '@/components/topup/RedeemCard'
import OnlineTopupCard from '@/components/topup/OnlineTopupCard'
import TopupHistoryDrawer from '@/components/topup/TopupHistoryDrawer'
import { renderQuota } from '@/utils/quota'

export default function TopUpPage() {
  const { t } = useTranslation()
  const { user, updateUser } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()

  const [info, setInfo] = useState<TopupInfo | null>(null)
  const [infoLoading, setInfoLoading] = useState(true)
  const [redeemKey, setRedeemKey] = useState('')
  const [redeemLoading, setRedeemLoading] = useState(false)
  const [payLoading, setPayLoading] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  const refreshUser = useCallback(async () => {
    try {
      const profile = await getUserInfoApi()
      updateUser(profile)
    } catch {
      // ignore
    }
  }, [updateUser])

  const loadInfo = useCallback(async () => {
    setInfoLoading(true)
    try {
      const data = await getTopupInfoApi()
      setInfo(data)
    } catch {
      setInfo({})
    } finally {
      setInfoLoading(false)
    }
  }, [])

  useEffect(() => {
    loadInfo()
    refreshUser()
  }, [loadInfo, refreshUser])

  useEffect(() => {
    if (searchParams.get('show_history') === 'true') {
      setHistoryOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete('show_history')
      setSearchParams(next, { replace: true })
      refreshUser()
    }
  }, [searchParams, setSearchParams, refreshUser])

  const handleRedeem = async () => {
    if (!redeemKey.trim()) {
      Message.warning(t('tt.topup.redeemPlaceholder'))
      return
    }
    setRedeemLoading(true)
    try {
      const quota = await redeemCodeApi(redeemKey.trim())
      Message.success(t('tt.topup.redeemSuccess', { quota: renderQuota(quota) }))
      updateUser({ quota: (user?.quota ?? 0) + quota })
      setRedeemKey('')
      await refreshUser()
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : t('tt.topup.redeemFailed'))
    } finally {
      setRedeemLoading(false)
    }
  }

  const handlePay = async (params: {
    channel: string
    amount: number
    payMoney: string | null
    creemProductId?: string
    waffoPayMethodIndex?: number
  }) => {
    setPayLoading(true)
    try {
      const { successUrl, cancelUrl } = getTopupReturnUrls()

      if (params.channel === 'stripe') {
        const link = await createStripeOrderApi({
          amount: params.amount,
          success_url: successUrl,
          cancel_url: cancelUrl,
        })
        window.open(link, '_blank')
        Message.info(t('tt.topup.stripeOpened'))
      } else if (params.channel === 'creem' && params.creemProductId) {
        const url = await createCreemOrderApi(params.creemProductId)
        window.location.href = url
      } else if (params.channel === 'waffo') {
        const url = await createWaffoOrderApi({
          amount: params.amount,
          pay_method_index: params.waffoPayMethodIndex,
        })
        window.location.href = url
      } else {
        const { url, params: formParams } = await createPayOrderApi({
          amount: params.amount,
          payment_method: params.channel,
        })
        submitEpayForm(url, formParams)
      }
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : t('tt.topup.orderFailed'))
      throw err
    } finally {
      setPayLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('tt.topup.title')}
        subtitle={t('tt.topup.subtitle')}
        actions={
          <Button icon={<IconHistory />} onClick={() => setHistoryOpen(true)}>
            {t('tt.topup.history')}
          </Button>
        }
      />

      <AccountStatsCard quota={user?.quota} usedQuota={user?.usedQuota} group={user?.group} />
      <RedeemCard
        value={redeemKey}
        loading={redeemLoading}
        onChange={setRedeemKey}
        onRedeem={handleRedeem}
      />
      <OnlineTopupCard info={info} loading={infoLoading} onPay={handlePay} payLoading={payLoading} />

      <TopupHistoryDrawer visible={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  )
}
